import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './_dashboard.module.scss';
import { AppBar, Badge, CircularProgress, Divider, Drawer, IconButton, Toolbar, Tooltip, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PermIdentity from '@mui/icons-material/PermIdentity';
import { Outlet, Route, Routes, useLocation, useNavigate } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import classNames from 'classnames';
import Sidebar from '../Sidebar/Sidebar';
import { IDashboardRoute } from '../../../Interfaces/IDashboardRoute';
import { ILoggedInUser } from '../../../Interfaces/ILoggedInUser';
import NotAuthorized from '../../NotAuthorized';
import { useConfiguration } from '../../../Data/ConfigurationData';
import {
  setBusinessRuleSetId,
  useBusinessRulesEngine,
  useBusinessRuleSetId,
  useBusinessRuleSets,
} from '../../../Data/BusinessRulesData';
import { showValidationDialog, ValidationDialog } from '../../../utils/ValidationDialog';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { getApplicant, isAdmin, validateApp } from '../../../utils/appUtils';
import Link from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import {
  cancelAppChanges,
  saveApp,
  updateApplicationFormConfigurations,
} from '../../../store/application/actions';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  loadGlobalSearchPage,
  loadGlobalSearchPageSuccess,
  loadingGlobalSearchPage,
  setGlobalSearchText,
  setIsSearching,
  SET_LOADING,
} from '../../../store/application/types';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { createStyles, Theme, ThemeProvider } from '@mui/material/styles';
import { useEmployeeRolePermissions } from '../../../Data/EmployeeRoleData';
import { usePostedAppLock } from '../../../hooks/usePostedAppLock';
import TuneIcon from '@mui/icons-material/Tune';
import { useDialog } from '../../../providers/DialogProvider';
import ApplicationFormConfiguration, {
  ApplicationFormResult,
} from '../../Applications/ApplicationConfiguration/ApplicationConfiguration';
import ReportViewerDialog from '../../Dialogs/ReportViewerDialog/ReportViewerDialog';
import ReportIcon from '@mui/icons-material/Report';
import SearchIcon from '@mui/icons-material/Search';
import { ApplicationFormConfig, LoadingPanel, NotificationDrawer, ReleaseNotes } from '@amsanalytics/react-shared-library';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { fetchData } from '../../../Data/fetch';
import { isEmpty, isNil } from 'ramda';
import ConfirmationDialog from '../../Dialogs/ConfirmationDialog';
import styled from 'styled-components';
import { ICustomField } from '../../../Interfaces/Common/ICustomField';
import {
  updateMultipleApplicationNotifications,
  updateSingleApplicationNotification,
} from '../../../store/Notifications/actions';
import { fetchConfig } from '../../../utils/HttpClient';
import { Constants } from '../../../utils/constants';
import { ILoggedInEmployee } from '../../../Interfaces/Admin/ILoggedInEmployee';
import AlertViewer from '../../AlertViewer/AlertViewer';
import { hasValue, addThousandsSeparator, ApplicationQuoteType } from '@amsanalytics/react-shared-library';
import { BrowserNotification } from '@amsanalytics/react-shared-library/build/Models/BrowserNotification';
import ReleaseNotesBanner from '../../ReleaseNotesBanner/ReleaseNotesBanner';
import localforage from 'localforage';
import { AppState } from '../../../store/rootReducer';
import { authService, successTheme } from '../../..';
import { ConfirmHardPullRequirement } from '../../Dialogs/ConfirmHardPullRequirement';
import { MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { IEmployeeApplicationHistory } from '../../../Interfaces/Common/IEmployeeApplicationHistory';
import { FilterDescriptor } from '@progress/kendo-data-query';
import SearchBar from '../../SearchBar/SearchBar';
import debounce from 'lodash.debounce';

interface IDashboardProps {
  routes: IDashboardRoute[];
}

export const HtmlTooltip = styled(Tooltip)`
  background-color: #f5f5f9;
  color: rgba(0, 0, 0, 0.87);
  max-width: 220px;
  font-size: 12px;
  border: 1px solid #dadde9;
`;

const Dashboard = (props: IDashboardProps) => {
  const dispatch = useDispatch();
  const { data: configuration } = useConfiguration();
  const [open, setOpen] = useState<boolean>(false);
  const [loading] = useState<boolean>(false);
  const [openDialog, closeDialog] = useDialog();
  const navigate = useNavigate();
  const helpDeskUrl = useSelector((state: AppState) => state.applications.lookups.helpDeskUrl);
  const employees = useSelector((state: AppState) => state.applications.employees);
  const activeUser: ILoggedInEmployee | undefined = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const currentEmployee = employees.find((e) => e.id === activeUser?.id);
  const applicationIsDirty = useSelector((state: AppState) => state.applications.appDirty);
  const financeQuoteIsDirty = useSelector((state: AppState) => state.applications.financeQuoteIsDirty);
  const leaseQuoteIsDirty = useSelector((state: AppState) => state.applications.applicationLeaseQuoteIsDirty);
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const [canView, canEdit, canAct, canActInStatus] = useEmployeeRolePermissions();
  const currentUser = useSelector((state: AppState) => state.applications.loggedInUser);
  const isGloballySearching = useSelector((state: AppState) => state.applications.isGloballySearchingApps);

  const applicantCreditAppFormConfiguration: ApplicationFormConfig | undefined = useSelector(
    (state: AppState) => state.applications.applicantCreditAppFormConfiguration
  );
  const coApplicantCreditAppFormConfiguration: ApplicationFormConfig | undefined = useSelector(
    (state: AppState) => state.applications.coApplicantCreditAppFormConfiguration
  );
  // List of custom fields for this client.
  const customFields: ICustomField[] = useSelector((state: AppState) =>
    state.applications.customFields.sort((a, b) => a.name.localeCompare(b.name))
  );
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location]);
  const employeeApplicationHistories: IEmployeeApplicationHistory[] = useSelector(
    (state: AppState) => state.applications.employeeApplicationHistories
  );
  const [recentAppFilter, setRecentAppFilter] = useState<FilterDescriptor | undefined>();
  const filterChange = (event: MultiSelectFilterChangeEvent) => {
    setRecentAppFilter(event.filter);
  };
  let filteredEmployeeApplicationHistories = employeeApplicationHistories
    .filter((h) => h.applicationId !== currentApp?.id)
    .slice(0, 10);

  const navigateToRecentApp = (event: MultiSelectChangeEvent) => {
    setRecentAppFilter(undefined);
    const newRecipients = event.value as IEmployeeApplicationHistory[];
    navigate(`/order/${newRecipients[0].applicationId}`);
  };

  // If the filter is defined, it will filter based on the name
  if (recentAppFilter) {
    filteredEmployeeApplicationHistories = filteredEmployeeApplicationHistories.filter((h) =>
      h.labelText
        .toLowerCase()
        .replace(/\s/g, '')
        .includes((recentAppFilter.value as string).toLowerCase().replace(/\s/g, ''))
    );
  }

  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState<boolean>(false);
  const loadComplete: boolean = !useSelector((state: AppState) => state.applications.loadingInitialData);
  const [deploymentDate, setDeploymentDate] = useState<string>('');
  const [siteName, setSiteName] = useState<string>('');

  const loggedInUser: ILoggedInUser | null | undefined = useSelector((state: AppState) => state.applications.loggedInUser);
  const browserNotifications: BrowserNotification[] = useSelector((state: AppState) => state.notifications.browserNotifications);
  const now = new Date();
  // list of dismissed release note IDs
  const [dismissedReleaseNotesId, setDismissedReleaseNotesId] = useState<string[]>([]);
  // Current release notes that have not been dismissed.
  const currentReleaseNotes: ReleaseNotes[] = useSelector((state: AppState) => state.applications.releaseNotes || []);
  // Get the release note ID to ignore from localforage
  const getReleaseNoteIdsToIgnore = async () => {
    const dismissedReleaseNotesIds = ((await localforage.getItem('dismissedReleaseNoteIds')) as string[]) || [];
    setDismissedReleaseNotesId(dismissedReleaseNotesIds);
  };

  // Get the release note ID to ignore from localforage on component mount
  useEffect(() => {
    getReleaseNoteIdsToIgnore();
  }, []);

  useEffect(() => {
    // Fetch deployment date from index.html last modified header
    fetch('/index.html', { method: 'HEAD', cache: 'no-cache' })
      .then(response => {
        const lastModified = response.headers.get('Last-Modified');
        if (lastModified) {
          // Parse UTC date and convert to local time
          const date = new Date(lastModified);
          setDeploymentDate(date.toLocaleString('en-US', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true
          }));
        }
      })
      .catch(err => console.error('Failed to fetch deployment date:', err));

    // Fetch API endpoint and determine site name
    fetch('/api-info.apisettings')
      .then(response => response.json())
      .then(config => {
        const apiEndpoint = config?.api_endpoint || '';
        const site = getSiteNameFromApiEndpoint(apiEndpoint);
        setSiteName(site);
      })
      .catch(err => console.error('Failed to fetch API config:', err));
  }, []);

  // Determine site name based on API endpoint
  const getSiteNameFromApiEndpoint = (apiEndpoint: string): string => {
    if (!apiEndpoint) return '';
    
    // Check for waterwheeldata.com (development)
    if (apiEndpoint.includes('waterwheeldata.com')) {
      return 'Site Development';
    }
    
    // Check for amsanalytics.com production sites
    const match = apiEndpoint.match(/losapi(\d+)\.amsanalytics\.com/);
    if (match && match[1]) {
      return `Site #${match[1]} Production`;
    }
    
    return 'Unknown Site';
  };

  const { data: businessRuleSetId } = useBusinessRuleSetId();
  const { data: businessRuleSets } = useBusinessRuleSets(activeUser);
  const businessRulesEngine = useBusinessRulesEngine();
  const logout = (bypassAppRequirements?: boolean) => {
    const result = businessRulesEngine({
      action: 'Logout',
    });
    if (result?.errors?.length && !bypassAppRequirements) {
      showValidationDialog({
        errors: result.errors,
        callback: () => logout(true),
        allowBypass: true,
      });
    } else {
      authService.SignOut();
    }
  };

  const is600 = useMediaQuery('(min-width:600px)');
  const is800 = useMediaQuery('(min-width:800px)');
  const is1000 = useMediaQuery('(min-width:1000px)');

  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string>('');

  const handleSaveApp = async (bypassAppRequirements: boolean, userBypassedSoftPullRequirement: boolean) => {
    const errors: string[] = validateApp(currentApp!);

    if (errors.length > 0) {
      const errs: string = errors.join(' ');
      setErrorMessages(errs);
      setShowError(true);
    } else {
      dispatch({ type: SET_LOADING, payload: true });
      setErrorMessages('');

      // Looks to see if the application has been saved elsewhere since it was opened
      const url = `api/Application/GetApplicationLastSaveDate/${currentApp!.id}`;
      const applicationLastSaveDate = await fetchData(url, dispatch);

      // Normalize via getTime(): either side may arrive as an ISO string or a Date,
      // and mixed-type `>` coerces the string to NaN — silently masking real conflicts.
      const serverStampMs = new Date(applicationLastSaveDate).getTime();
      const localStampMs = currentApp!.modifiedDate ? new Date(currentApp!.modifiedDate).getTime() : serverStampMs;

      if (serverStampMs > localStampMs) {
        dispatch({ type: SET_LOADING, payload: false });
        openDialog({
          fullScreen: true,
          children: (
            <ConfirmationDialog
              closeDialog={closeDialog} // cancel
              prompt='The application has been saved by someone else since you opened it. Continue saving anyway?'
              title='Save and Overwrite Application?'
              accept={async () => {
                dispatch({ type: SET_LOADING, payload: true });
                closeDialog();
                await saveApplication(bypassAppRequirements, userBypassedSoftPullRequirement);
              }}
            />
          ),
        });
      } else {
        const leaseQuoteHasUnsolvedEdits = currentApp!.quoteType === ApplicationQuoteType.Lease && leaseQuoteIsDirty;
        if (financeQuoteIsDirty || leaseQuoteHasUnsolvedEdits) {
          dispatch({ type: SET_LOADING, payload: false });
          openDialog({
            fullScreen: true,
            children: (
              <ConfirmationDialog
                closeDialog={closeDialog} // cancel
                prompt={leaseQuoteHasUnsolvedEdits
                  ? "The lease quote has not been solved since it was edited. Save the edits without solving?"
                  : "The application's quote hasn't been run since the inputs were edited. Continue saving and refresh the quote?"}
                title={leaseQuoteHasUnsolvedEdits ? 'Save Without Solving?' : 'Save and Overwrite Quote?'}
                accept={async () => {
                  dispatch({ type: SET_LOADING, payload: true });
                  closeDialog();
                  await saveApplication(bypassAppRequirements, userBypassedSoftPullRequirement);
                }}
              />
            ),
          });
        } else {
          await saveApplication(bypassAppRequirements, userBypassedSoftPullRequirement);
        }
      }
    }
  };

  const saveApplication = async (bypassAppRequirements: boolean, userBypassedSoftPullRequirement: boolean) => {
    const result = businessRulesEngine({
      action: 'Save',
    });
    if (result?.errors?.length && !bypassAppRequirements) {
      dispatch({ type: SET_LOADING, payload: false });
      showValidationDialog({
        errors: result.errors,
        callback: () => handleSaveApp(true, false),
      });
    } else {
      dispatch({ type: SET_LOADING, payload: true });
      await dispatch(saveApp(businessRulesEngine, userBypassedSoftPullRequirement));
    }
  };

  const handleCancel = (bypassAppRequirements?: boolean) => {
    const result = businessRulesEngine({
      action: 'Cancel',
    });
    if (result?.errors?.length && !bypassAppRequirements) {
      showValidationDialog({
        errors: result.errors,
        callback: () => handleCancel(true),
      });
    } else {
      dispatch(cancelAppChanges());
    }
  };

  const onSaveButtonClick = async () => {
    cancelGlobalSearch();
    let userBypassedSoftPullRequirement: boolean | undefined;
    // If the next status is Desking, it'll check to see if there need to be any hard pulls
    if (currentApp!.appStatusId.toLowerCase() === Constants.applicationStatuses.desking.toLowerCase()) {
      // Check to see if any of the applicants have only had a soft pull
      userBypassedSoftPullRequirement = await ConfirmHardPullRequirement(currentApp!, openDialog, closeDialog);
      if (userBypassedSoftPullRequirement === false) {
        return;
      }
    }
    await handleSaveApp(false, userBypassedSoftPullRequirement ?? false);
  };

  const onCancelButtonClick = () => {
    cancelGlobalSearch();
    handleCancel();
  };

  const handleErrorClose = (event: React.SyntheticEvent<any> | Event, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowError(false);
  };

  const handleAlertClose = (event: React.SyntheticEvent<any>) => {
    setShowError(false);
  };

  /**
   * SignalR Websockets
   */
  // useEffect(() => {
  //   if(currentApp) {
  //     const lockedApplication = lockedApplications?.find(la => la.applicationId === currentApp?.id && la.connectionId === signalRConnectionId);
  //     if(!lockedApplication) {
  //       lockApplication(currentApp.id);
  //     }
  //     const lockedApplicationsForMe = lockedApplications.filter(la => la.connectionId === signalRConnectionId && la.applicationId !== currentApp.id);
  //     if(lockedApplicationsForMe.length > 0) {
  //       unlockApplications(lockedApplicationsForMe.map(la => la.applicationId));
  //     }
  //   } else {
  //     // Unlock any applications for this user
  //     const lockedApplicationsForMe = lockedApplications.filter(la => la.connectionId === signalRConnectionId);
  //     if(lockedApplicationsForMe.length > 0) {
  //       unlockApplications(lockedApplicationsForMe.map(la => la.applicationId));
  //     }
  //   }
  // }, [currentApp]);

  // useEffect(() => {
  //   const lockedApplication = lockedApplications?.find(la => la.applicationId === currentApp?.id);
  //   setApplicationLockInfo(lockedApplication);
  // }, [lockedApplications, signalRConnectionId, currentApp]);

  // const lockApplication = (applicationId: string) => {
  //   if(connection && connection.state === HubConnectionState.Connected) {
  //     connection.invoke("LockApplication", applicationId);
  //   }
  // };

  // const unlockApplication = (applicationId: string) => {
  //   if(connection && connection.state === HubConnectionState.Connected) {
  //     connection.invoke("UnlockApplication", applicationId);
  //   }
  // };
  // const unlockApplications = (applicationIds: string[]) => {
  //   if(connection && connection.state === HubConnectionState.Connected) {
  //     connection.invoke("UnlockApplications", applicationIds);
  //   }
  // };
  /********************************End SignalR Websockets********************************************/

  const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
  const { activeUnlock } = usePostedAppLock();
  const canEditInStatus = (): boolean => {
    return currentApp?.unlockedBySuperUser || !!activeUnlock || canActInStatus(currentApp?.appStatusId || '', 'Save');
  };

  const clearNotification = async (notificationId: string) => {
    dispatch(updateSingleApplicationNotification(notificationId));
  };

  const clearNotifications = async (notificationIds: string[]) => {
    dispatch(updateMultipleApplicationNotifications(notificationIds));
  };

  // returns the server name which maps to the hostname.


  const applicantsWithoutCreditConsent =
    currentApp?.appCustomers.filter((ac) => ac.customer.customerCredit.some((cc) => !hasValue(cc.acknowledgementDate))).length ??
    0;

  const consentGivenString =
    configuration?.featureFlags?.[FeatureFlagIds.CustomerWillAcknowledgeCreditApplicationInformation] === true &&
    currentApp &&
    applicantsWithoutCreditConsent > 0
      ? ' No Consent Given For Credit Pull - '
      : '';

  const canViewRoute = (route: IDashboardRoute): boolean => {
    if (route.publicRoute) {
      return true;
    }
    if (route.adminRoute && isAdmin(currentEmployee)) {
      return true;
    }
    if (route.superUserRoute && configuration?.isSuperUser) {
      return true;
    }
    return false;
  };

  const releaseNotes = currentReleaseNotes.filter(
    (releaseNote: ReleaseNotes) =>
      !dismissedReleaseNotesId.includes(releaseNote.id) &&
      new Date(releaseNote.startDate) <= now &&
      now < new Date(releaseNote.expirationDate)
  );

  // Get global search page data using global saerch bar input after 1 second.
  const globalSearch = (searchText: string) => {
    dispatch(setGlobalSearchText(searchText));
    if (isNil(searchText) || isEmpty(searchText)) {
      // If user clears the search text, clear out the global search results
      dispatch(loadingGlobalSearchPage(false));
      dispatch(loadGlobalSearchPageSuccess({ applications: [], count: 0 }));
    } else {
      dispatch(loadingGlobalSearchPage(true));
      // Search for contacts based on user's search input
      dispatch(loadGlobalSearchPage({ searchText: searchText, skip: 0, take: 20 }));
    }
  };

  const cancelGlobalSearch = () => {
    dispatch(setIsSearching(false));
    dispatch(loadingGlobalSearchPage(false));
    dispatch(setGlobalSearchText(''));
    dispatch(loadGlobalSearchPageSuccess({ applications: [], count: 0 }));
  };

  // Search debounce to prevent unnecessary service calls.
  let globalSearchDebounce = useRef(
    debounce(async (searchText) => {
      globalSearch(searchText);
    }, 500)
  ).current;

  // Clean up the debounce function
  useEffect(() => {
    return () => {
      globalSearchDebounce.cancel();
    };
  }, [globalSearchDebounce]);

  return (
    <div className={styles.dashboardContainer}>
      {loggedInUser?.employee ? (
        <div className={styles.root}>
          <AppBar position='static' className={styles.appBar}>
            <Toolbar disableGutters={true} className={styles.toolbar} color='primary'>
              <IconButton
                color='inherit'
                aria-label='Open drawer'
                onClick={() => {
                  cancelGlobalSearch();
                  setOpen(true);
                }}
              >
                <MenuIcon />
              </IconButton>

              {is800 && configuration?.clientLogoUrl && (
                <img
                  src={configuration?.clientLogoUrl}
                  style={{
                    maxHeight: '50px',
                    margin: '5px 10px 5px 5px',
                    borderRadius: '5px',
                  }}
                  alt='Client Logo'
                />
              )}
              <Breadcrumbs style={{ color: 'white' }}>
                <Typography component='h1' variant='h6' color='inherit' noWrap className={styles.title}>
                  <Link
                    component={RouterLink}
                    to='/'
                    color='inherit'
                    underline='hover'
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    LOS {is600 && configuration?.clientName ? `- ${configuration?.clientName}` : undefined}
                  </Link>
                </Typography>
                <Routes>
                  {props.routes
                    .filter((r) => canViewRoute(r))
                    .map((route, key) => (
                      <Route
                        key={key}
                        path={route.path}
                        element={
                          <Typography component='h1' variant='h6' color='inherit' noWrap className={styles.title}>
                            <Link component={RouterLink} to={route.path} color='inherit' underline='hover'>
                              {route.sidebarName !== 'Order'
                                ? route.sidebarName
                                : currentApp && primary
                                  ? 'App #' +
                                    addThousandsSeparator(currentApp.applicationNo) +
                                    ' - ' +
                                    consentGivenString +
                                    primary.customer.firstName +
                                    ' ' +
                                    primary.customer.lastName
                                  : 'App'}
                            </Link>
                          </Typography>
                        }
                      />
                    ))}
                </Routes>
              </Breadcrumbs>
              {applicationIsDirty && canEditInStatus() && (
                <ThemeProvider theme={successTheme}>
                  {is800 ? (
                    <>
                      <Button
                        style={{ marginLeft: 20 }}
                        startIcon={<SaveIcon fontSize='small' />}
                        onClick={onSaveButtonClick}
                        variant='contained'
                        color='primary'
                      >
                        Save
                      </Button>
                      <Button
                        style={{ marginLeft: 10 }}
                        startIcon={<CancelIcon fontSize='small' />}
                        onClick={onCancelButtonClick}
                        variant='contained'
                        color='error'
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <IconButton
                        aria-label='Save'
                        title='Save'
                        onClick={onSaveButtonClick}
                        style={{ color: 'white', background: successTheme.palette.primary.main, marginLeft: 10 }}
                      >
                        <SaveIcon fontSize='small' />
                      </IconButton>
                      <IconButton
                        aria-label='Cancel'
                        title='Cancel'
                        onClick={onCancelButtonClick}
                        style={{ color: 'white', background: successTheme.palette.error.main, marginLeft: 10 }}
                      >
                        <CancelIcon fontSize='small' />
                      </IconButton>
                    </>
                  )}
                </ThemeProvider>
              )}

              <div style={{ flexGrow: 1 }}></div>
              <MultiSelect // Multiselect provides better interaction with a keyboard with filtering and selecting
                data={filteredEmployeeApplicationHistories}
                onChange={(event: MultiSelectChangeEvent) => {
                  cancelGlobalSearch();
                  navigateToRecentApp(event);
                }}
                value={[]}
                filterable
                onFilterChange={filterChange}
                textField='labelText'
                placeholder={'Recent Applications'}
                style={{ width: '250px', marginRight: '15px' }}
              />
              {isGloballySearching && (
                <SearchBar
                  autoFocus
                  style={{ marginLeft: '10px', marginRight: '15px' }}
                  placeholder='Search applications...'
                  onEscapeKey={() => {
                    cancelGlobalSearch();
                    dispatch(setIsSearching(false));
                  }}
                  onSearchInputChange={(searchText: string) => {
                    globalSearchDebounce(searchText.trim());
                  }}
                />
              )}
              {!isGloballySearching && (
                <Tooltip title='Search Applications'>
                  <IconButton color='inherit' onClick={() => dispatch(setIsSearching(true))}>
                    <SearchIcon style={{ color: 'white' }} />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                onClick={() => setIsNotificationDrawerOpen(true)}
                size='small'
                aria-label='show notifications'
                color='inherit'
                disabled={browserNotifications.length < 1}
              >
                <Badge badgeContent={browserNotifications.length} color='error' variant='standard'>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              {hasValue(configuration?.crmUrl) && (currentUser as any)?.isCrmUser && (
                <Tooltip title='CRM'>
                  <Link className={styles.crmLinkButton} href={`https://${configuration?.crmUrl ?? ''}`} target='_blank'>
                    <Typography variant='caption'>CRM</Typography>
                  </Link>
                </Tooltip>
              )}
              {/* <StatusMenu
                style={{ background: 'white' }}
                onStatusChange={(status: Availability) => {
                  const statusId = getAvailabilityStatusId(status);
                  if (statusId) {
                    dispatch(updateEmployeeStatus(statusId));
                  }
                }}
                status={getAvailabilityEnumValue(loggedInUser.employee.availabilityStatusId)}
              /> */}
              {!isNil(helpDeskUrl) && isAdmin(currentEmployee) && (
                <Tooltip title='Help Desk'>
                  <Link
                    to='/ticket'
                    target='_self'
                    component={RouterLink}
                    // onClick={() => {
                    //   cancelGlobalSearch();
                    // }}
                  >
                    <HelpOutlineIcon fontSize='medium' style={{ color: '#ffffff', marginLeft: '15px', marginTop: '5px' }} />
                  </Link>
                </Tooltip>
              )}

              <IconButton onClick={() => logout()} style={{ color: 'white' }}>
                <PermIdentity />
                <Typography variant='button'>{loggedInUser ? <>Logout {loggedInUser.employee.firstName}</> : null}</Typography>
              </IconButton>
            </Toolbar>
          </AppBar>
          <Drawer
            variant='temporary'
            classes={{
              paper: classNames(styles.drawerPaper, !open && styles.drawerPaperClose)
            }}
            open={open}
            onClose={() => setOpen(false)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div>
                <div className={styles.toolbarIcon}>
                  <IconButton onClick={() => setOpen(false)}>
                    <ChevronLeftIcon />
                  </IconButton>
                </div>
                <Divider />
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <Sidebar routes={props.routes} currentUser={currentUser} />
            {
              // Allow admins to edit the application form configuration.
              (configuration?.isSuperUser || isAdmin(currentUser?.employee)) && (
                <Button
                  style={{
                    paddingLeft: `16px`,
                    paddingRight: `16px`,
                    width: `100%`,
                    display: `flex`,
                    position: `relative`,
                    boxSizing: `border-box`,
                    textAlign: `left`,
                    alignItems: `center`,
                    paddingTop: `8px`,
                    color: `#f500f57`,
                    paddingBottom: `8px`,
                    justifyContent: `flex-start`,
                    textDecoration: `none`,
                  }}
                  onClick={() => {
                    setOpen(false);
                    openDialog({
                      fullScreen: true,
                      children: (
                        <ApplicationFormConfiguration
                          applicantCreditAppFormConfiguration={applicantCreditAppFormConfiguration}
                          coApplicantCreditAppFormConfiguration={coApplicantCreditAppFormConfiguration}
                          customFields={customFields}
                          accept={(result: ApplicationFormResult) => {
                            // Update the application form configuration.
                            dispatch(updateApplicationFormConfigurations(result));
                            closeDialog();
                          }}
                          closeDialog={closeDialog}
                        />
                      ),
                    });
                  }}
                >
                  <div style={{ display: `inline-flex`, minWidth: `56px` }}>
                    <TuneIcon style={{ color: `rgba(0, 0, 0, 0.54)` }} />
                  </div>
                  <span
                    style={{
                      color: `#0000EE`,
                      fontSize: `1rem`,
                      fontWeight: 400,
                      textTransform: `initial`,
                      letterSpacing: 'initial',
                    }}
                  >
                    App Config
                  </span>
                </Button>
              )
            }
            {configuration?.clientId === 'CFI' && (
              <>
                <Button
                  className={styles.configButton}
                  onClick={async () => {
                    const bearerToken = await authService.getToken();
                    const config: any = await fetchConfig();
                    const baseUrl = `${config.api_endpoint}`;
                    openDialog({
                      children: (
                        <ReportViewerDialog
                          title={'Turn Down Letters'}
                          reportName={'CFITurndownLetterContainer.trdp'}
                          baseUrl={baseUrl}
                          clientId={configuration?.clientId}
                          parameters={
                            [
                                // {
                                //   name: "ApplicationNumber",
                              //   value: currentApp?.applicationNo,
                              // },
                            ]
                          }
                          closeDialog={closeDialog}
                          bearerToken={bearerToken!}
                        />
                      ),
                      fullScreen: true,
                    });
                  }}
                >
                  <div style={{ display: `inline-flex`, minWidth: `56px` }}>
                    <ReportIcon style={{ color: `rgba(0, 0, 0, 0.54)` }} />
                  </div>
                  <span className={styles.configButtonText}>Print Denials</span>
                </Button>
                <Button
                  className={styles.configButton}
                  onClick={async () => {
                    const bearerToken = await authService.getToken();
                    const config: any = await fetchConfig();
                    const baseUrl = `${config.api_endpoint}`;
                    openDialog({
                      children: (
                        <ReportViewerDialog
                          title={'Turn Down Letters'}
                          reportName={'CFIThirtyDayTurndownLetterContainer.trdp'}
                          baseUrl={baseUrl}
                          clientId={configuration?.clientId}
                          parameters={
                            [
                              // {
                              //   name: "ApplicationNumber",
                              //   value: currentApp?.applicationNo,
                              // },
                            ]
                          }
                          closeDialog={closeDialog}
                          bearerToken={bearerToken!}
                        />
                      ),
                      fullScreen: true,
                    });
                  }}
                >
                  <div style={{ display: `inline-flex`, minWidth: `56px` }}>
                    <ReportIcon style={{ color: `rgba(0, 0, 0, 0.54)` }} />
                  </div>
                  <span className={styles.configButtonText}>Print 30 Day Denials</span>
                </Button>
              </>
            )}
                <Divider />
                {is1000 && configuration?.isSuperUser && (
                  <Box display='flex' justifyContent='flex-end' alignItems='flex-end'>
                    <TextField
                      select
                      variant='filled'
                      size='small'
                      label='Business Rule Set'
                      style={{
                        backgroundColor: 'white',
                        width: 250,
                      }}
                      value={businessRuleSetId === 0 ? 0 : businessRuleSetId || null}
                      onChange={(e) => setBusinessRuleSetId(e.target.value as any)}
                      InputLabelProps={{
                        shrink: typeof businessRuleSetId === 'number',
                      }}
                    >
                      <MenuItem>&nbsp;</MenuItem>
                      {currentEmployee &&
                        (businessRuleSets || [])?.map((ruleSet, key) => (
                          <MenuItem key={key} value={ruleSet.id}>
                            {ruleSet.description}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Box>
                )}
              </div>
              <div style={{ padding: '16px', borderTop: '1px solid rgba(0, 0, 0, 0.12)', marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <Typography variant='caption' style={{ textAlign: 'center', color: 'rgba(0, 0, 0, 0.6)' }}>
                  &copy; {new Date().getFullYear()} Auto Master
                  {siteName && ` (${siteName})`}
                </Typography>
                {deploymentDate && (
                  <Typography variant='caption' style={{ textAlign: 'center', color: 'rgba(0, 0, 0, 0.6)' }}>
                    Deployed: {deploymentDate}
                  </Typography>
                )}
              </div>
            </div>
          </Drawer>
          {releaseNotes && releaseNotes.length > 0 && (
            <ReleaseNotesBanner
              releaseNotes={releaseNotes}
              onDismiss={(releaseNotesId: string) => {
                // Add the release note ID to the list of dismissed release notes in localforage
                localforage.setItem('dismissedReleaseNoteIds', [...dismissedReleaseNotesId, releaseNotesId]);
                setDismissedReleaseNotesId([...dismissedReleaseNotesId, releaseNotesId]);
              }}
            />
          )}
          <main className={styles.content}>
            {loading && (
              <LoadingPanel label='Loading...' style={{ background: 'white', left: 'calc(50% - 100px)', marginTop: '20%' }} />
            )}
            {/* This is where all of the sub routes are rendered */}
            <Outlet />
          </main>
          <Snackbar open={showError} autoHideDuration={6000} onClose={handleErrorClose}>
            <Alert onClose={handleAlertClose} severity='error'>
              {errorMessages}
            </Alert>
          </Snackbar>
          <NotificationDrawer
            isOpen={isNotificationDrawerOpen}
            onClearNotification={clearNotification}
            onClearNotifications={clearNotifications}
            onClose={() => setIsNotificationDrawerOpen(false)}
            notifications={browserNotifications}
            onClickNotification={(notification: BrowserNotification) => {
              if (hasValue(notification.losLink)) {
                setIsNotificationDrawerOpen(false);
                navigate(notification.losLink!);
              }
            }}
          />
          <AlertViewer />
        </div>
      ) : loadComplete ? (
        <NotAuthorized />
      ) : (
        <LoadingPanel label={'Loading ...'} />
      )}
      <ValidationDialog />
    </div>
  );
};

export default Dashboard;
